<div class="matches-page">
  <!-- Page Header -->
  <div class="page-header-section">
    <div class="header-left">
      <button
        class="back-btn"
        (click)="goBack()"
        aria-label="Back to Business Needs"
        type="button"
      >
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M19 12H5M12 19l-7-7 7-7"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <div class="page-header-content">
        <span class="page-badge">Funding Matches</span>
        <h1 class="page-title">Your Matches</h1>
        <p class="page-subtitle">
          Review organizations matched to your business profile and explore
          external funding opportunities.
        </p>
      </div>
    </div>
  </div>

  <!-- Primary Tab Bar (Flat, single-level) -->
  <div class="tabs-container">
    <div class="tabs-nav" role="tablist">
      <!-- Tab 1: Internal Matches (Always visible) -->
      <button
        type="button"
        role="tab"
        class="tab-btn"
        [class.active]="activeTab === 'internal'"
        [attr.aria-selected]="activeTab === 'internal'"
        (click)="setTab('internal')"
      >
        <span>Internal Matches</span>
        @if (!isLoadingInternal && internalMatches.length > 0) {
          <span class="tab-count">{{ internalMatches.length }}</span>
        }
      </button>

      <!-- Tab 2: External Matches (Shown only if external matches or applied leads exist) -->
      @if (canShowExternalMatchesTab) {
        <button
          type="button"
          role="tab"
          class="tab-btn"
          [class.active]="activeTab === 'external'"
          [attr.aria-selected]="activeTab === 'external'"
          (click)="setTab('external')"
        >
          <span>External Matches</span>
          @if (!isLoadingExternalCounts && externalMatchesCount > 0) {
            <span class="tab-count">{{ externalMatchesCount }}</span>
          }
        </button>
      }

      <!-- Tab 3: Applied Leads (Shown only if applied leads exist) -->
      @if (canShowAppliedLeadsTab) {
        <button
          type="button"
          role="tab"
          class="tab-btn"
          [class.active]="activeTab === 'applied'"
          [attr.aria-selected]="activeTab === 'applied'"
          (click)="setTab('applied')"
        >
          <span>Applied Leads</span>
          @if (!isLoadingExternalCounts && appliedLeadsCount > 0) {
            <span class="tab-count count-applied">{{ appliedLeadsCount }}</span>
          }
        </button>
      }
    </div>
  </div>

  <!-- Tab Content: Internal Matches -->
  @if (activeTab === "internal") {
    <div class="tab-content" role="tabpanel">
      @if (isLoadingInternal) {
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Loading matched organizations...</p>
        </div>
      } @else if (hasInternalError) {
        <div class="error-state">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
              fill="currentColor"
            />
          </svg>
          <h3>Unable to load matches</h3>
          <p>Something went wrong while fetching your matches.</p>
          <button
            type="button"
            class="retry-btn"
            (click)="loadInternalMatches()"
          >
            Retry
          </button>
        </div>
      } @else if (hasNoProfile) {
        <div class="empty-state">
          <div class="empty-icon-wrap">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0H5m2 0v-5h10v5"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <h3 class="empty-title">No Business Profile Yet</h3>
          <p class="empty-desc">
            Create a business profile first to start receiving matched
            organizations.
          </p>
          <button
            type="button"
            class="empty-action-btn"
            (click)="navigateToBusinessProfile()"
          >
            Go to Business Needs
          </button>
        </div>
      } @else if (internalMatches.length === 0) {
        <div class="empty-state">
          <div class="empty-icon-wrap">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21 21l-4.35-4.35M19 11a8 8 0 11-16 0 8 8 0 0116 0z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <h3 class="empty-title">No Internal Matches Yet</h3>
          <p class="empty-desc">
            No organizations have been matched to your business profile yet.
            Ensure your profile attributes and products are up to date to
            increase matches.
          </p>
          <button
            type="button"
            class="empty-action-btn"
            (click)="navigateToBusinessProfile()"
          >
            View Business Profile
          </button>
        </div>
      } @else {
        <!-- Internal Matches List with Compact Rows -->
        <div class="internal-matches-section">
          <!-- Helpful Info Callout Banner -->
          <div class="matches-info-callout">
            <div class="callout-icon-wrap">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div class="callout-content">
              <span class="callout-title"
                >No action is required from you at this time</span
              >
              <p class="callout-text">
                Matched organizations have access to your profile and will
                review your lending needs. They may contact you directly if
                there is a mutual fit.
              </p>
            </div>
          </div>

          <div class="matches-header-info">
            <p class="matches-summary-text">
              <strong>{{ internalMatches.length }}</strong>
              {{
                internalMatches.length === 1 ? "organization" : "organizations"
              }}
              matched your lending profile.
            </p>
          </div>

          <div class="compact-matches-list">
            @for (conn of internalMatches; track conn.id) {
              <div class="compact-match-row">
                <!-- Col 1: Avatar & Organization Name -->
                <div class="match-col-main">
                  <div class="org-avatar org-avatar-initial">
                    {{
                      getOrgInitial(conn.display_name || conn.connection_name)
                    }}
                  </div>
                  <div class="org-details">
                    <h3
                      class="org-name"
                      [title]="conn.display_name || conn.connection_name"
                    >
                      {{ conn.display_name || conn.connection_name }}
                    </h3>
                    <span class="org-type-label">CDFI Partner</span>
                  </div>
                </div>

                <!-- Col 2: Minimized Stat Pills with Rich Hover Popovers -->
                <div class="match-col-stats">
                  <!-- Programs Pill -->
                  <div class="stat-pill" tabindex="0">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <span class="stat-pill-label">
                      <strong>{{ conn.matched_criterias.length }}</strong>
                      {{
                        conn.matched_criterias.length === 1
                          ? "Program"
                          : "Programs"
                      }}
                    </span>

                    <!-- Hover Popover: Programs Details -->
                    <div class="detail-hover-popup" role="tooltip">
                      <div class="hover-popup-header">
                        <span class="hover-popup-title">Matched Programs</span>
                        <span class="hover-popup-count">{{
                          conn.matched_criterias.length
                        }}</span>
                      </div>
                      <div class="hover-popup-body">
                        @if (conn.matched_criterias.length > 0) {
                          <div class="hover-items-list">
                            @for (
                              crit of conn.matched_criterias;
                              track crit.name
                            ) {
                              <div class="hover-program-item">
                                <div class="hover-program-header">
                                  <span
                                    class="hover-item-name"
                                    [title]="crit.name"
                                    >{{ crit.name }}</span
                                  >
                                  <span
                                    class="hover-item-score"
                                    [ngClass]="
                                      getMatchPercentageClass(
                                        crit.match_percentage
                                      )
                                    "
                                  >
                                    {{ crit.match_percentage }}% match
                                  </span>
                                </div>
                                <div class="hover-progress-track">
                                  <div
                                    class="hover-progress-bar"
                                    [ngClass]="
                                      getMatchPercentageClass(
                                        crit.match_percentage
                                      )
                                    "
                                    [style.width.%]="crit.match_percentage || 0"
                                  ></div>
                                </div>
                              </div>
                            }
                          </div>
                        } @else {
                          <span class="hover-empty-text"
                            >No specific program details available.</span
                          >
                        }
                      </div>
                    </div>
                  </div>

                  <!-- Products Pill -->
                  <div class="stat-pill" tabindex="0">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V5a2 2 0 012-2z"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <span class="stat-pill-label">
                      <strong>{{ getAllProducts(conn).length }}</strong>
                      {{
                        getAllProducts(conn).length === 1
                          ? "Product"
                          : "Products"
                      }}
                    </span>

                    <!-- Hover Popover: Products Details -->
                    <div class="detail-hover-popup" role="tooltip">
                      <div class="hover-popup-header">
                        <span class="hover-popup-title">Matched Products</span>
                        <span class="hover-popup-count">{{
                          getAllProducts(conn).length
                        }}</span>
                      </div>
                      <div class="hover-popup-body">
                        @if (getAllProducts(conn).length > 0) {
                          <div class="hover-products-chips">
                            @for (prod of getAllProducts(conn); track prod) {
                              <span class="hover-product-chip">
                                <svg
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M5 13l4 4L19 7"
                                    stroke="currentColor"
                                    stroke-width="2.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  />
                                </svg>
                                {{ prod }}
                              </span>
                            }
                          </div>
                        } @else {
                          <span class="hover-empty-text"
                            >Standard loan &amp; grant products available.</span
                          >
                        }
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Col 3: Match Badge & View Profile CTA -->
                <div class="match-col-action">
                  @if (getBestMatchPercentage(conn) > 0) {
                    <span
                      class="compact-match-badge"
                      [ngClass]="
                        getMatchPercentageClass(getBestMatchPercentage(conn))
                      "
                    >
                      {{ getBestMatchPercentage(conn) }}% Match
                    </span>
                  }
                  @if (conn.connection_name) {
                    <button
                      type="button"
                      class="btn-view-profile-compact"
                      (click)="navigateToCdfiProfile(conn.connection_name)"
                    >
                      <span>View Profile</span>
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </button>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      }
    </div>
  }

  <!-- Tab Content: External Matches or Applied Leads (Embedded mode without inner tab bar) -->
  @if (activeTab === "external" || activeTab === "applied") {
    <div class="tab-content external-tab-content" role="tabpanel">
      <app-external-matches
        [forcedTab]="activeTab === 'applied' ? 'applied' : 'matches'"
        (tabChange)="onExternalTabChange($event)"
        (countsChange)="onCountsChange($event)"
      ></app-external-matches>
    </div>
  }
</div>
